/**
 * Org Branding settings page — b2b-3-1
 *
 * Route: /org/settings/branding
 *
 * Features:
 * - Upload primary_color, accent_color, app_name to staging (PUT /orgs/branding)
 * - Upload logo, logo_dark, login_bg image files to GCS staging
 * - Preview staged changes (GET /orgs/branding/preview)
 * - Live color preview swatches
 * - Publish staged branding (POST /orgs/branding/publish)
 * - Shows current live branding alongside staged preview
 */

import React, { useState, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../services/api';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8001';

async function uploadBrandingFormData(formData: FormData): Promise<BrandingUploadResponse> {
  const token = localStorage.getItem('token');
  const res = await fetch(`${API_URL}/api/v1/orgs/branding`, {
    method: 'PUT',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: formData,
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ detail: 'Upload failed' }));
    throw new Error(err.detail || 'Upload failed');
  }
  return res.json() as Promise<BrandingUploadResponse>;
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface BrandingPreviewResponse {
  app_name: string | null;
  primary_color: string | null;
  accent_color: string | null;
  logo_url: string | null;
  logo_dark_url: string | null;
  login_bg_url: string | null;
  staging_updated_at: string | null;
  has_staged_changes: boolean;
}

interface BrandingUploadResponse {
  org_id: number;
  staging_primary_color: string | null;
  staging_accent_color: string | null;
  staging_app_name: string | null;
  staging_logo_url: string | null;
  staging_logo_dark_url: string | null;
  staging_login_bg_url: string | null;
  staging_updated_at: string | null;
  message: string;
}

interface OrgBranding {
  app_name: string;
  primary_color: string;
  accent_color: string;
  logo_url: string | null;
  logo_dark_url: string | null;
  login_bg_url: string | null;
}

// ---------------------------------------------------------------------------
// API helpers
// ---------------------------------------------------------------------------

const brandingApi = {
  // Fetch active (live) branding
  getActive: () => api.request<OrgBranding>('/api/v1/orgs/branding'),

  // Fetch staging preview
  getPreview: () => api.request<BrandingPreviewResponse>('/api/v1/orgs/branding/preview'),

  // Upload to staging (multipart/form-data)
  upload: uploadBrandingFormData,

  // Publish staging → active
  publish: () =>
    api.request('/api/v1/orgs/branding/publish', { method: 'POST' }),
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function ColorSwatch({ color, label }: { color: string | null; label: string }) {
  if (!color) return null;
  return (
    <div className="flex items-center gap-2">
      <div
        className="w-8 h-8 rounded border border-slate-600"
        style={{ backgroundColor: color }}
        title={color}
      />
      <div>
        <p className="text-xs text-slate-400">{label}</p>
        <p className="text-sm font-mono text-slate-200">{color}</p>
      </div>
    </div>
  );
}

function LogoPreview({ url, label }: { url: string | null; label: string }) {
  if (!url) {
    return (
      <div className="w-32 h-16 bg-slate-800 border border-slate-700 rounded flex items-center justify-center">
        <p className="text-xs text-slate-500">{label} — none</p>
      </div>
    );
  }
  return (
    <div>
      <p className="text-xs text-slate-400 mb-1">{label}</p>
      <img
        src={url}
        alt={label}
        className="h-16 object-contain border border-slate-700 rounded p-1 bg-slate-800"
        onError={(e) => {
          (e.target as HTMLImageElement).style.display = 'none';
        }}
      />
    </div>
  );
}

function FileInput({
  label,
  name,
  onChange,
  accept = 'image/png,image/jpeg,image/svg+xml,image/webp',
}: {
  label: string;
  name: string;
  onChange: (file: File | null) => void;
  accept?: string;
}) {
  const ref = useRef<HTMLInputElement>(null);
  const [filename, setFilename] = useState<string | null>(null);

  return (
    <div>
      <label className="block text-sm text-slate-300 mb-1">{label}</label>
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => ref.current?.click()}
          className="text-sm px-3 py-1.5 bg-slate-700 hover:bg-slate-600 text-slate-200 rounded border border-slate-600 transition"
        >
          Choose file
        </button>
        <span className="text-xs text-slate-400 truncate max-w-[180px]">
          {filename ?? 'No file selected'}
        </span>
      </div>
      <input
        ref={ref}
        type="file"
        name={name}
        accept={accept}
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0] ?? null;
          setFilename(file?.name ?? null);
          onChange(file);
        }}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main page component
// ---------------------------------------------------------------------------

export default function OrgBranding() {
  const queryClient = useQueryClient();

  // Form state
  const [primaryColor, setPrimaryColor] = useState('');
  const [accentColor, setAccentColor] = useState('');
  const [appName, setAppName] = useState('');
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoDarkFile, setLogoDarkFile] = useState<File | null>(null);
  const [loginBgFile, setLoginBgFile] = useState<File | null>(null);

  const [saveSuccess, setSaveSuccess] = useState(false);
  const [publishSuccess, setPublishSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Queries
  const { data: activeBranding, isLoading: loadingActive } = useQuery({
    queryKey: ['org-branding'],
    queryFn: brandingApi.getActive,
  });

  const { data: preview, isLoading: loadingPreview } = useQuery({
    queryKey: ['org-branding-preview'],
    queryFn: brandingApi.getPreview,
  });

  // Upload mutation
  const uploadMutation = useMutation({
    mutationFn: (formData: FormData) => brandingApi.upload(formData),
    onSuccess: () => {
      setSaveSuccess(true);
      setError(null);
      queryClient.invalidateQueries({ queryKey: ['org-branding-preview'] });
      setTimeout(() => setSaveSuccess(false), 3000);
    },
    onError: (err: Error) => {
      setError(err.message || 'Upload failed');
    },
  });

  // Publish mutation
  const publishMutation = useMutation({
    mutationFn: brandingApi.publish,
    onSuccess: () => {
      setPublishSuccess(true);
      setError(null);
      queryClient.invalidateQueries({ queryKey: ['org-branding'] });
      queryClient.invalidateQueries({ queryKey: ['org-branding-preview'] });
      setTimeout(() => setPublishSuccess(false), 3000);
    },
    onError: (err: Error) => {
      setError(err.message || 'Publish failed');
    },
  });

  function handleSaveToStaging(e: React.FormEvent) {
    e.preventDefault();
    setError(null);

    const formData = new FormData();
    if (primaryColor.trim()) formData.append('primary_color', primaryColor.trim());
    if (accentColor.trim()) formData.append('accent_color', accentColor.trim());
    if (appName.trim()) formData.append('app_name', appName.trim());
    if (logoFile) formData.append('logo', logoFile);
    if (logoDarkFile) formData.append('logo_dark', logoDarkFile);
    if (loginBgFile) formData.append('login_bg', loginBgFile);

    // Must provide at least one field
    const hasContent =
      primaryColor.trim() ||
      accentColor.trim() ||
      appName.trim() ||
      logoFile ||
      logoDarkFile ||
      loginBgFile;

    if (!hasContent) {
      setError('Please provide at least one field to update.');
      return;
    }

    uploadMutation.mutate(formData);
  }

  function handlePublish() {
    setError(null);
    publishMutation.mutate();
  }

  if (loadingActive) {
    return (
      <div className="p-6 text-slate-400">Loading branding settings...</div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto p-6 space-y-8">
      <div>
        <h1 className="text-2xl font-semibold text-white">White-Label Branding</h1>
        <p className="text-sm text-slate-400 mt-1">
          Customise your organisation's brand identity. Changes are saved to staging
          and only go live after you publish.
        </p>
      </div>

      {/* Current live branding */}
      <div className="bg-slate-800 border border-slate-700 rounded-lg p-5">
        <h2 className="text-lg font-medium text-white mb-4">Current Live Branding</h2>
        {activeBranding ? (
          <div className="space-y-4">
            <p className="text-sm text-slate-300">
              App name: <span className="font-medium text-white">{activeBranding.app_name}</span>
            </p>
            <div className="flex gap-6">
              <ColorSwatch color={activeBranding.primary_color} label="Primary color" />
              <ColorSwatch color={activeBranding.accent_color} label="Accent color" />
            </div>
            <div className="flex gap-4 flex-wrap">
              <LogoPreview url={activeBranding.logo_url} label="Logo" />
              <LogoPreview url={activeBranding.logo_dark_url} label="Logo (dark)" />
              <LogoPreview url={activeBranding.login_bg_url} label="Login background" />
            </div>
          </div>
        ) : (
          <p className="text-sm text-slate-400">No custom branding configured. Using my investments defaults.</p>
        )}
      </div>

      {/* Staged preview */}
      {!loadingPreview && preview?.has_staged_changes && (
        <div className="bg-amber-900/20 border border-amber-700/50 rounded-lg p-5">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-lg font-medium text-amber-300">Staged Changes</h2>
              <p className="text-xs text-amber-400/80 mt-0.5">
                These changes are not yet live. Publish to apply them.
              </p>
            </div>
            <button
              onClick={handlePublish}
              disabled={publishMutation.isPending}
              className="px-4 py-2 bg-amber-600 hover:bg-amber-500 text-white text-sm font-medium rounded transition disabled:opacity-50"
            >
              {publishMutation.isPending ? 'Publishing...' : 'Publish Now'}
            </button>
          </div>
          <div className="space-y-4">
            {preview.app_name && (
              <p className="text-sm text-slate-300">
                App name: <span className="font-medium text-white">{preview.app_name}</span>
              </p>
            )}
            <div className="flex gap-6">
              <ColorSwatch color={preview.primary_color} label="Primary color" />
              <ColorSwatch color={preview.accent_color} label="Accent color" />
            </div>
            <div className="flex gap-4 flex-wrap">
              <LogoPreview url={preview.logo_url} label="Logo (staged)" />
              <LogoPreview url={preview.logo_dark_url} label="Logo dark (staged)" />
              <LogoPreview url={preview.login_bg_url} label="Login BG (staged)" />
            </div>
          </div>
          {publishSuccess && (
            <p className="text-sm text-green-400 mt-3">Branding published successfully.</p>
          )}
        </div>
      )}

      {/* Upload form */}
      <div className="bg-slate-800 border border-slate-700 rounded-lg p-5">
        <h2 className="text-lg font-medium text-white mb-4">Upload New Branding</h2>
        <form onSubmit={handleSaveToStaging} className="space-y-5">
          {/* App name */}
          <div>
            <label className="block text-sm text-slate-300 mb-1">
              App / brand name <span className="text-slate-500">(max 100 chars)</span>
            </label>
            <input
              type="text"
              value={appName}
              onChange={(e) => setAppName(e.target.value)}
              maxLength={100}
              placeholder="e.g. Acme Investments"
              className="w-full bg-slate-700 border border-slate-600 rounded px-3 py-2 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-teal-500"
            />
          </div>

          {/* Colors */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-slate-300 mb-1">Primary color</label>
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  value={primaryColor || '#1DB954'}
                  onChange={(e) => setPrimaryColor(e.target.value)}
                  className="w-10 h-9 rounded border border-slate-600 bg-slate-700 cursor-pointer"
                  title="Pick primary color"
                />
                <input
                  type="text"
                  value={primaryColor}
                  onChange={(e) => setPrimaryColor(e.target.value)}
                  placeholder="#1DB954"
                  pattern="^#[0-9A-Fa-f]{6}$"
                  className="flex-1 bg-slate-700 border border-slate-600 rounded px-3 py-2 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-teal-500 font-mono"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm text-slate-300 mb-1">Accent color</label>
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  value={accentColor || '#F0A500'}
                  onChange={(e) => setAccentColor(e.target.value)}
                  className="w-10 h-9 rounded border border-slate-600 bg-slate-700 cursor-pointer"
                  title="Pick accent color"
                />
                <input
                  type="text"
                  value={accentColor}
                  onChange={(e) => setAccentColor(e.target.value)}
                  placeholder="#F0A500"
                  pattern="^#[0-9A-Fa-f]{6}$"
                  className="flex-1 bg-slate-700 border border-slate-600 rounded px-3 py-2 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-teal-500 font-mono"
                />
              </div>
            </div>
          </div>

          {/* Live color preview */}
          {(primaryColor || accentColor) && (
            <div className="flex gap-4 p-3 bg-slate-700/50 rounded border border-slate-600">
              <p className="text-xs text-slate-400 self-center">Preview:</p>
              {primaryColor && <ColorSwatch color={primaryColor} label="Primary" />}
              {accentColor && <ColorSwatch color={accentColor} label="Accent" />}
            </div>
          )}

          {/* Logo files */}
          <div className="grid grid-cols-1 gap-4">
            <FileInput label="Logo (light mode)" name="logo" onChange={setLogoFile} />
            <FileInput label="Logo (dark mode)" name="logo_dark" onChange={setLogoDarkFile} />
            <FileInput label="Login background image" name="login_bg" onChange={setLoginBgFile} />
          </div>

          <p className="text-xs text-slate-500">
            Accepted formats: PNG, JPEG, SVG, WebP. Max size: 5 MB per file.
          </p>

          {error && (
            <p className="text-sm text-red-400">{error}</p>
          )}

          {saveSuccess && (
            <p className="text-sm text-green-400">
              Branding saved to staging. Review the staged preview above and publish when ready.
            </p>
          )}

          <div className="flex gap-3">
            <button
              type="submit"
              disabled={uploadMutation.isPending}
              className="px-5 py-2 bg-teal-600 hover:bg-teal-500 text-white text-sm font-medium rounded transition disabled:opacity-50"
            >
              {uploadMutation.isPending ? 'Saving...' : 'Save to Staging'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
